/**
 * Guide Modal Web Component
 * Usage: <guide-modal guide-url="{{ routes.root_url }}pages/full-guide"></guide-modal>
 */
class GuideModal extends HTMLElement {
  constructor() {
    super();
    this.guideContentCache = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.guidePageUrl = this.getAttribute('guide-url');

    // Move modal container to body so it escapes any parent stacking context (e.g. side-drawer)
    this._container = this.querySelector('[data-modal-container]');
    if (this._container) {
      document.body.appendChild(this._container);
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this._container) return;

    const backdropClickable = this._container.querySelector('[data-backdrop-clickable]');
    const closeBtn = this._container.querySelector('[data-close]');

    // Close on backdrop click
    backdropClickable?.addEventListener('click', (e) => {
      if (e.target === backdropClickable) {
        this.close();
      }
    });

    // Close on button click
    closeBtn?.addEventListener('click', () => this.close());

    // Close on escape key
    this.handleEscape = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.handleEscape);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleEscape);
    this._container?.remove();
  }

  async open() {
    if (this._container) {
      this._container.style.display = 'block';
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
    }

    // If content is cached, use it
    if (this.guideContentCache) {
      this.updateContent(this.guideContentCache);
      return;
    }

    // Fetch content from the guide page
    try {
      await this.fetchContent();
    } catch (error) {
      console.error('Error loading guide:', error);
      this.updateContent('<p>Failed to load guide. Please try again later.</p>');
    }
  }

  close() {
    if (this._container) {
      this._container.style.display = 'none';
      document.body.style.overflow = '';
      this.isOpen = false;
    }
  }

  async fetchContent() {
    const response = await fetch(this.guidePageUrl);
    if (!response.ok) {
      throw new Error('Failed to load guide content');
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract the guide content
    const guideContent = doc.querySelector('.how-to-guide-modal-content');

    if (guideContent) {
      this.guideContentCache = guideContent.outerHTML;
      this.updateContent(this.guideContentCache);
    } else {
      this.updateContent('<p>Guide content not found.</p>');
    }
  }

  updateContent(content) {
    const contentContainer = this._container.querySelector('[data-content]');
    const loading = this._container.querySelector('[data-loading]');

    if (loading) {
      loading.style.display = 'none';
    }

    if (contentContainer) {
      contentContainer.innerHTML = content;
    }
  }
}

customElements.define('guide-modal', GuideModal);

class GuideModalTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const target = this.getAttribute('modal') || 'guide-modal';
      const modal = document.querySelector(target);
      if (modal) {
        modal.open();
      }
    });
  }
}

customElements.define('guide-modal-trigger', GuideModalTrigger);
